import React, { useCallback, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

export const FIELD_COLORS = {
  text: 'border-sky-500 bg-sky-400/15',
  date: 'border-amber-500 bg-amber-400/15',
  checkbox: 'border-emerald-500 bg-emerald-400/15',
  signature: 'border-rose-500 bg-rose-400/15',
  initials: 'border-fuchsia-500 bg-fuchsia-400/15',
};

export const FIELD_LABELS = { text: 'Text', date: 'Date', checkbox: 'Checkbox', signature: 'Signature', initials: 'Initials' };

/** Measures the container width so pages can be rendered "fit to width" × zoom. Returns [callbackRef, width]. */
export const useContainerWidth = () => {
  const [width, setWidth] = useState(0);
  const roRef = useRef(null);
  const ref = useCallback((el) => {
    if (roRef.current) { roRef.current.disconnect(); roRef.current = null; }
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    roRef.current = ro;
    setWidth(el.clientWidth);
  }, []);
  return [ref, width];
};

/**
 * Renders every page of a PDF stacked vertically.
 * `renderOverlay(pageIndex, pxWidth, pxHeight)` returns absolutely positioned children for that page.
 */
const PdfPages = ({ file, pages, pageWidth, renderOverlay, onPagePointerDown, pageRefs }) => {
  const [loadError, setLoadError] = useState(null);
  if (!file) return <div className="py-16 text-center text-sm text-muted-foreground">Loading document…</div>;
  return (
    <Document
      file={file}
      loading={<div className="py-16 text-center text-sm text-muted-foreground">Rendering PDF…</div>}
      onLoadError={(e) => setLoadError(e?.message || 'Failed to load PDF')}
      error={<div className="py-16 text-center text-sm text-destructive">{loadError || 'Failed to load PDF'}</div>}
    >
      <div className="flex flex-col items-center gap-6">
        {(pages || []).map((p, i) => {
          const h = pageWidth * (p.height / p.width);
          return (
            <div
              key={i}
              ref={(el) => { if (pageRefs) pageRefs.current[i] = el; }}
              className="relative shadow-lg ring-1 ring-black/10 bg-white select-none"
              style={{ width: pageWidth, height: h }}
              onPointerDown={onPagePointerDown ? (e) => onPagePointerDown(e, i) : undefined}
              data-testid={`pdf-page-${i}`}
            >
              <Page pageNumber={i + 1} width={pageWidth} renderTextLayer={false} renderAnnotationLayer={false} />
              <div className="absolute inset-0">{renderOverlay(i, pageWidth, h)}</div>
              <span className="absolute -bottom-5 right-0 text-[11px] text-muted-foreground">Page {i + 1} of {pages.length}</span>
            </div>
          );
        })}
      </div>
    </Document>
  );
};

export default PdfPages;
